import { fieldTypeLabels, usedInLabels } from "./copy";
import type {
  ResolvedFieldConfig,
  TaxonomyDefinition,
  TaxonomyFieldType,
  TaxonomyOption,
  TaxonomyUsedIn,
  TaxonomyValues,
} from "./types";

export function normalizeTaxonomyName(value: string): string {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

export function trimTaxonomyInput(value: string): string {
  return String(value || "").trim().replace(/\s+/g, " ");
}

export function fieldTypeLabel(type: string): string {
  return fieldTypeLabels[type] || type;
}

export function usedInLabel(usedIn: string): string {
  return usedInLabels[usedIn] || usedIn;
}

export function isSelectType(type?: string): boolean {
  return type === "SINGLE_SELECT" || type === "MULTI_SELECT";
}

export function eligibleForContext(
  usedIn: TaxonomyUsedIn | string | undefined,
  context: "PRODUCT" | "PROCESS"
): boolean {
  if (usedIn === "BOTH") return true;
  return usedIn === context;
}

export function isEmptyTaxonomyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "boolean") return false;
  if (typeof value === "number") return Number.isNaN(value);
  if (typeof value === "object") {
    const rich = value as { text?: unknown; media?: unknown };
    const hasText = typeof rich.text === "string" && rich.text.trim().length > 0;
    const hasMedia = Array.isArray(rich.media) && rich.media.some((item) => {
      if (!item || typeof item !== "object") return false;
      const media = item as { mediaId?: unknown; id?: unknown; type?: unknown };
      const id = typeof media.mediaId === "string" ? media.mediaId : media.id;
      return typeof id === "string" && id.trim().length > 0
        && (media.type === "IMAGE" || media.type === "VIDEO" || media.type === "AUDIO");
    });
    return !hasText && !hasMedia;
  }
  return false;
}

export function missingRequiredOnPublish(
  fields: ResolvedFieldConfig[],
  values: TaxonomyValues | undefined
): ResolvedFieldConfig[] {
  const current = values || {};
  return (fields || [])
    .filter((field) => field.enabled && field.requiredOnPublish)
    .filter((field) => isEmptyTaxonomyValue(current[field.taxonomyId]))
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
}

export function mergeTaxonomyValues(
  previous: TaxonomyValues | undefined,
  next: TaxonomyValues | undefined,
  activeIds: string[]
): TaxonomyValues {
  const out: TaxonomyValues = { ...(previous || {}) };
  for (const id of activeIds || []) {
    if (!next || !Object.prototype.hasOwnProperty.call(next, id)) continue;
    const value = next[id];
    // Omitted keys preserve server history; explicit empties clear editable values.
    out[id] = value === undefined ? null : value;
  }
  return out;
}

export function findExactNameMatch(
  name: string,
  definitions: TaxonomyDefinition[]
): TaxonomyDefinition | undefined {
  const needle = normalizeTaxonomyName(name);
  if (!needle) return undefined;
  return (definitions || []).find((row) => normalizeTaxonomyName(row.name) === needle);
}

export function findNearNameMatches(
  name: string,
  definitions: TaxonomyDefinition[],
  limit = 5
): TaxonomyDefinition[] {
  const needle = normalizeTaxonomyName(name);
  if (needle.length < 2) return [];
  return (definitions || [])
    .filter((row) => {
      const hay = normalizeTaxonomyName(row.name);
      return hay !== needle && (hay.includes(needle) || needle.includes(hay));
    })
    .slice(0, limit);
}

export function activeOptions(options?: TaxonomyOption[]): TaxonomyOption[] {
  return (options || []).filter((opt) => opt.active !== false);
}

export function optionLabel(
  options: TaxonomyOption[] | undefined,
  value: unknown
): string {
  if (Array.isArray(value)) {
    return value.map((id) => optionLabel(options, id)).filter(Boolean).join(", ");
  }
  const id = String(value || "");
  const match = (options || []).find((opt) => opt.id === id);
  return match?.label || id;
}

export function displayTaxonomyValue(
  field: Pick<ResolvedFieldConfig, "fieldType" | "options" | "unit">,
  value: unknown
): string {
  if (isEmptyTaxonomyValue(value)) return "—";
  if (field.fieldType === "BOOLEAN") return value ? "Yes" : "No";
  if (field.fieldType === "SINGLE_SELECT" || field.fieldType === "MULTI_SELECT") {
    return optionLabel(field.options, value);
  }
  if (field.fieldType === "RICH_CONTENT" && value && typeof value === "object") {
    const rich = value as { text?: unknown; media?: unknown[] };
    const parts = [];
    if (typeof rich.text === "string" && rich.text.trim()) parts.push(rich.text.trim());
    if (Array.isArray(rich.media) && rich.media.length) {
      parts.push(`${rich.media.length} media ${rich.media.length === 1 ? "item" : "items"}`);
    }
    return parts.join(" · ") || "—";
  }
  const text = Array.isArray(value) ? value.join(", ") : String(value);
  return field.unit ? `${text} ${field.unit}` : text;
}

export function isWeaveTypeApplicable(source: unknown): boolean {
  if (!source || typeof source !== "object") return true;
  const row = source as { weaveTypeApplicable?: unknown };
  return row.weaveTypeApplicable !== false;
}

export function historicalFields(
  values: TaxonomyValues | undefined,
  active: ResolvedFieldConfig[],
  definitions: TaxonomyDefinition[]
): Array<ResolvedFieldConfig & { archived: boolean }> {
  const activeIds = new Set((active || []).map((row) => row.taxonomyId));
  const defById = new Map((definitions || []).map((row) => [row.id, row]));
  const activeById = new Map((active || []).map((row) => [row.taxonomyId, row]));
  return Object.keys(values || {})
    .filter((id) => !activeIds.has(id) || activeById.get(id)?.enabled === false)
    .map((id) => {
      const def = defById.get(id);
      const live = activeById.get(id);
      return {
        taxonomyId: id,
        name: live?.name || def?.name || id,
        description: live?.description || def?.description,
        fieldType: (live?.fieldType || def?.fieldType || "TEXT") as TaxonomyFieldType,
        usedIn: (live?.usedIn || def?.usedIn || "PRODUCT") as TaxonomyUsedIn,
        options: live?.options || def?.options || [],
        unit: live?.unit ?? def?.unit ?? null,
        source: live?.source || def?.source || "STUDIO",
        enabled: false,
        requiredOnPublish: false,
        displayOrder: live?.displayOrder || 0,
        visibility: live?.visibility || "INTERNAL",
        archived: true,
      };
    });
}

export function parseCustomFieldsRequired(res: any): string[] {
  const data = res?.data || res;
  if (data?.code === "CUSTOM_FIELDS_REQUIRED" && Array.isArray(data?.missingTaxonomyIds)) {
    return data.missingTaxonomyIds.map(String);
  }
  if (res?.code === "CUSTOM_FIELDS_REQUIRED" && Array.isArray(res?.missingTaxonomyIds)) {
    return res.missingTaxonomyIds.map(String);
  }
  return [];
}

export function focusTaxonomyField(taxonomyId: string, focusId?: string): void {
  const el = document.getElementById(focusId || `hkp-tax-${taxonomyId}`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  const input = el.querySelector("input, select, textarea, [tabindex]") as HTMLElement | null;
  input?.focus?.();
}
